import { compareTwoStrings } from "string-similarity";
import Constants from "const/Constants";
import objectHash from "object-hash";

export default class Objects {
  static getPropertyByPath(object, path, fallback) {
    const pathFragments = (path ? (Array.isArray(path) ? path : path.split(".")) : []);

    if (!pathFragments.length) return fallback;

    const result = pathFragments.reduce((res, key) => res && res[key], object);

    return result === undefined ? fallback : result;
  }

  static getProps(object, keys, defaults = {}) {
    return keys.reduce((result, key) => {
      const value = Objects.getPropertyByPath(object, key, defaults[key]);

      if (value !== undefined) result[key] = value;

      return result;
    }, {});
  }

  static checkSimilarityBy(searchBy = {}, target, similarityCoef = Constants.DATA_SIMILARITY_COEFFICIENT) {
    const { id, ...restSearchBy } = searchBy;

    const fieldsList = Object.entries(restSearchBy);

    if (id && id === target.id) return true;
    if (fieldsList.length === 0) return false;
    if (similarityCoef === 1) {
      return fieldsList.some(([fieldKey, fieldValue]) => {
        if (!target[fieldKey] || !fieldValue) return false;

        return target[fieldKey].trim().toLowerCase() === fieldValue.trim().toLowerCase();
      });
    }

    return fieldsList.some(([fieldKey, fieldValue]) => {
      const stringA = target[fieldKey] && `${target[fieldKey]}`.trim().toLowerCase();

      const stringB = fieldValue && `${fieldValue}`.trim().toLowerCase();

      return (stringA && fieldValue) && (stringA === stringB || compareTwoStrings(stringA, stringB) > similarityCoef);
    });
  }

  static checkDeepEqualityByHash(objectA, objectB) {
    return objectHash({ value: objectA }) === objectHash({ value: objectB });
  }

  static checkDeepEqualityByProps(objectA, objectB) {
    if (objectA === objectB) return true;
    if (!objectA || !objectB) return false;

    const typeStringA = Object.prototype.toString.call(objectA);

    const typeStringB = Object.prototype.toString.call(objectB);

    if (typeStringA !== typeStringB || typeof objectA !== "object") {
      return false;
    }
    if (Array.isArray(objectA)) {
      return objectA.length === objectB.length
        && objectA.every((item, index) => Objects.checkDeepEqualityByProps(item, objectB[index]));
    }

    const keysA = Object.keys(objectA);

    const keysB = Object.keys(objectB);

    return keysA.length === keysB.length
      && keysA.every((key) => Objects.checkDeepEqualityByProps(objectA[key], objectB[key]));
  }

  static map(target, iterator) {
    if (!target) return target;
    if (Array.isArray(target)) return target.map(iterator);

    return Object.fromEntries(
      Object.entries(target).map(([key, value], ...args) => {
        return [key, iterator(value, ...args)];
      })
    );
  }
}
